<template>
  <b-modal :visible="value" title="Bus SOS Settings" size="lg" scrollable
           @change="v => $emit('input', v)" @show="load_data">
    <div v-if="loading" class="text-center py-4"><b-spinner></b-spinner></div>
    <div v-else class="ssm">
      <!-- yard-only provider (no telematics): display-only flags set by support, never saved from here -->
      <div v-if="isYardOnly" class="ssm-yard-only">
        <span class="ssm-badge">Yard-only provider</span>
        <span class="ssm-yard-only-text">Your company has no vehicle tracking; SOS requests start from a pin dropped on your driver link or the Send SOS page.</span>
      </div>
      <div class="ssm-row">
        <label class="ssm-label">Enabled</label>
        <b-form-select v-model="model.bus_sos_enabled" :options="boolean_options" class="ssm-bool"/>
      </div>
      <div class="ssm-row">
        <label class="ssm-label">Notify Me Of Nearby SOS</label>
        <b-form-select v-model="model.bus_sos_receive_from_other_companies" :options="boolean_options" class="ssm-bool"/>
        <div class="ssm-help">Get alerted when another company's SOS is near your yards or vehicles.</div>
      </div>
      <div class="ssm-row">
        <label class="ssm-label">Send To Other Companies</label>
        <b-form-select v-model="model.bus_sos_send_to_other_companies" :options="boolean_options" class="ssm-bool"/>
      </div>
      <div class="ssm-row">
        <label class="ssm-label">Email Recipients</label>
        <b-form-input v-model="model.bus_sos_email_recipients" placeholder="comma-separated emails"/>
      </div>
      <div class="ssm-row">
        <label class="ssm-label">SMS Recipients</label>
        <b-form-input v-model="model.bus_sos_sms_recipients" placeholder="comma-separated phone numbers"/>
      </div>
      <div class="ssm-row">
        <label class="ssm-label">Escalation Phone Numbers</label>
        <b-form-input v-model="model.bus_sos_escalation_phone_numbers" placeholder="notified if not opened in time"/>
      </div>
      <div class="ssm-row">
        <label class="ssm-label">Escalation Delay (min)</label>
        <b-form-input v-model="model.bus_sos_escalation_minutes" type="number" min="1" max="15" placeholder="5" class="ssm-num"/>
      </div>
      <div class="ssm-row">
        <label class="ssm-label">Range In Miles</label>
        <b-form-input v-model="model.bus_sos_max_miles_away" type="number" min="0" max="500" class="ssm-num"/>
      </div>
      <!-- a yard-only company has no vehicles to tag-match -->
      <div v-if="!isYardOnly" class="ssm-row">
        <label class="ssm-label">Include Vehicles Matching Tag</label>
        <b-form-input v-model="model.bus_sos_include_all_vehicles_with_matching_tag"/>
      </div>

      <template v-if="isEnabled">
        <hr/>
        <div class="ssm-slug-title">Driver SOS Link</div>
        <div class="ssm-help">A page drivers open on their phone to send an SOS from a map pin. Pick a link name, then share it. Changing the name later disables the old link.</div>
        <div class="ssm-slug-row">
          <span class="ssm-slug-prefix">https://web.fleet-integrations.com/#/sos/</span>
          <b-form-input v-model="slug_input" placeholder="e.g. anderson-coach" class="ssm-slug-input"
                        @input="slug_input = ($event || '').toLowerCase()"/>
          <b-button size="sm" variant="primary" @click="save_sos_slug" :disabled="sos_slug_loading || !slug_input">
            <b-spinner v-if="sos_slug_loading" small></b-spinner>&nbsp;Save link
          </b-button>
        </div>
        <div v-if="sos_slug_url" class="ssm-slug-row">
          <b-form-input :value="sos_slug_url" readonly class="ssm-slug-url" @focus="$event.target.select()"/>
          <b-button size="sm" variant="outline-secondary" @click="copy_sos_link">{{ sos_copied ? 'Copied!' : 'Copy' }}</b-button>
        </div>
      </template>
    </div>

    <template #modal-footer>
      <b-button variant="secondary" @click="$emit('input', false)">Cancel</b-button>
      <b-button variant="success" :disabled="saving || loading" @click="save">
        <b-spinner v-if="saving" small></b-spinner>&nbsp;{{ saving ? 'Saving…' : 'Save' }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { get_tenant_config_settings, update_tenant_config_settings, set_sos_form_slug } from '../networking/apis'
import { mapState } from 'vuex'

const BOOL_KEYS = ['bus_sos_enabled', 'bus_sos_send_to_other_companies', 'bus_sos_receive_from_other_companies']
// the keys this modal reads AND writes back (exported for the unit test only)
export const MANAGED_KEYS = [...BOOL_KEYS, 'bus_sos_email_recipients', 'bus_sos_sms_recipients',
  'bus_sos_escalation_phone_numbers', 'bus_sos_escalation_minutes',
  'bus_sos_max_miles_away', 'bus_sos_include_all_vehicles_with_matching_tag']
// support-controlled flags shown for context; never part of the save payload
export const DISPLAY_KEYS = ['bus_sos_yard_only']

export default {
  name: 'sos_settings_modal',
  props: {
    value: { type: Boolean, default: false },  // v-model: modal visibility
  },
  data() {
    return {
      loading: true,
      saving: false,
      model: {},
      slug_input: '',
      sos_slug_url: null,
      sos_slug_loading: false,
      sos_copied: false,
      boolean_options: [{ text: 'True', value: 'True' }, { text: 'False', value: 'False' }],
    }
  },
  computed: {
    ...mapState({ user: state => state.profile.user }),
    isEnabled() {
      const v = this.model.bus_sos_enabled
      return v === true || v === 'True'
    },
    isYardOnly() {
      // get_config stringifies scalars, so the flag arrives as 'True'/'False'
      const v = this.model.bus_sos_yard_only
      return v === true || v === 'True'
    },
  },
  methods: {
    async load_data() {
      this.loading = true
      try {
        const { value: { data = {} } } = await get_tenant_config_settings(this.user.tenant)
        const results = data.results || {}
        const model = {}
        MANAGED_KEYS.forEach(k => { model[k] = results[k] })
        BOOL_KEYS.forEach(k => { model[k] = (model[k] === true || model[k] === 'True') ? 'True' : 'False' })
        model.bus_sos_slug = results.bus_sos_slug
        // company-name-derived default ("Bus Company" -> "bus-company") to pre-fill the link field
        model.bus_sos_slug_default = results.bus_sos_slug_default
        DISPLAY_KEYS.forEach(k => { model[k] = results[k] })
        this.model = model
        this.init_sos_slug()
      } catch (e) {
        this.$emit('showAlert', 'Could not load Bus SOS settings — try again or contact support.', 'danger', 6000)
      } finally {
        this.loading = false
      }
    },
    async save() {
      this.saving = true
      try {
        const payload = {}
        MANAGED_KEYS.forEach(k => { if (k in this.model) payload[k] = this.model[k] })
        await update_tenant_config_settings(this.user.tenant, payload)
        this.$emit('showAlert', 'Bus SOS settings saved.', 'success', 5000)
        this.$emit('saved')
        this.$emit('input', false)
      } catch (e) {
        this.$emit('showAlert', 'Could not save Bus SOS settings — try again.', 'danger', 6000)
      } finally {
        this.saving = false
      }
    },
    init_sos_slug() {
      const slug = this.model && this.model.bus_sos_slug
      // default the field to the company-name slug when no link is set yet, so the admin can accept
      // it with one click. The copyable "your link" URL only reflects a SAVED slug (a default isn't
      // live until saved).
      this.slug_input = slug || (this.model && this.model.bus_sos_slug_default) || ''
      this.sos_slug_url = slug ? 'https://web.fleet-integrations.com/#/sos/' + slug : null
    },
    async save_sos_slug() {
      if (!this.slug_input) return
      const changing = this.model.bus_sos_slug && this.model.bus_sos_slug !== this.slug_input
      if (changing && !window.confirm('Change the link name? The current link will stop working for anyone who has it bookmarked.')) return
      this.sos_slug_loading = true
      try {
        const { value: { data = {} } } = await set_sos_form_slug(this.user.tenant, this.slug_input)
        this.slug_input = data.slug
        this.sos_slug_url = data.url
        this.model.bus_sos_slug = data.slug
        this.$emit('showAlert', 'Driver SOS link saved. Share it with your drivers.', 'success', 5000)
      } catch (e) {
        this.$emit('showAlert', 'Could not save the driver SOS link.', 'danger', 6000)
      } finally {
        this.sos_slug_loading = false
      }
    },
    copy_sos_link() {
      if (!this.sos_slug_url) return
      try {
        navigator.clipboard.writeText(this.sos_slug_url)
        this.sos_copied = true
        setTimeout(() => { this.sos_copied = false }, 2000)
      } catch (e) {
        this.$emit('showAlert', 'Copy failed — select the link and copy it manually.', 'warning', 5000)
      }
    },
  },
}
</script>

<style scoped>
.ssm-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}
.ssm-label {
  flex: 0 0 200px;
  text-align: right;
  font-weight: 600;
  color: #47566a;
  margin: 0;
}
.ssm-row .form-control,
.ssm-row .custom-select {
  flex: 1 1 220px;
  min-width: 0;
}
.ssm-bool,
.ssm-num {
  flex: 0 0 120px;
}
.ssm-help {
  flex: 1 1 100%;
  margin-left: 212px;
  font-size: 12px;
  color: #8295a7;
}
.ssm-yard-only {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #47566a;
}
.ssm-badge {
  flex: 0 0 auto;
  display: inline-block;
  padding: 3px 9px;
  border-radius: 999px;
  background: #e8eef5;
  color: #23608f;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}
.ssm-slug-title {
  font-weight: 700;
  color: #1c2733;
  margin-bottom: 4px;
}
.ssm-slug-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 10px;
}
.ssm-slug-prefix {
  color: #6e7e8f;
}
.ssm-slug-input {
  flex: 0 1 240px;
}
.ssm-slug-url {
  flex: 1 1 360px;
}
</style>
